import { AxiosError } from 'axios';
import * as api from '../../api/authApi';
import { endpoints } from '../../config/endpoints';
import httpStatusCodes from '../../constants/http-status-codes';
import { isTokenDecodable, isTokenUsable } from '../../helpers/jwt-helper';
import { ensureMsSsoToken, MsSsoTokenProvider } from '../../helpers/ms-sso-helper';
import * as storageHelper from '../../helpers/storage-helper';
import ErrorCode, { ErrorCodes } from '../../models/error-code';
import SmgToken from '../../models/smg-token';
import * as actionCreators from '../action-creators/authenticate-action-creators';
import * as commonActionCreators from '../action-creators/common-action-creators';
import { BaseAction } from '../model/action';

const doAuthentication =
  (code: string, successCallback: Function, failureCallback: Function) =>
  async (dispatch: (action: BaseAction) => void) => {
    try {
      dispatch(commonActionCreators.acSetLoadingIndicator(true));
      await api.getAccessToken(code).then((response) => {
        if (response.status !== httpStatusCodes.OK) {
          dispatch(actionCreators.acSetIsAuthenticated(false));
          dispatch(commonActionCreators.acSetLoadingIndicator(false));
          if (failureCallback) {
            failureCallback();
          }
          return;
        }
        const smgToken = response.data;
        storageHelper.setSmgToken(smgToken);
      }).catch(() => {
        dispatch(actionCreators.acSetIsAuthenticated(false));
        dispatch(commonActionCreators.acSetLoadingIndicator(false));
        if (failureCallback) {
          failureCallback();
          return;
        }
      });
      
      const msTeamsToken = storageHelper.getMsSsoToken();
      if (!msTeamsToken) {
        if (failureCallback) {
          failureCallback();
        }
        return;
      }

      await api.authenticate().then((response) => {
        if (response.status === httpStatusCodes.OK) {
          api.updateSmgTokens();
          dispatch(actionCreators.acSetIsAuthenticated(true));
          dispatch(commonActionCreators.acSetLoadingIndicator(false));
          if (successCallback) {
            successCallback();
          }
        }
      }).catch(() => {
        dispatch(commonActionCreators.acSetLoadingIndicator(false));
        dispatch(actionCreators.acSetIsAuthenticated(false));
        if (failureCallback) {
          failureCallback();
        }
        return;
      });
    } catch (error) {
      if ((error as AxiosError).response?.status == 401) {
        dispatch(commonActionCreators.acSetLoadingIndicator(false));
        dispatch(actionCreators.acSetIsAuthenticated(false));
        if (failureCallback) {
          failureCallback();
        }
      }
      return;
    }
  };

const removeAuthentication = () => async (dispatch: (action: BaseAction) => void) => {
  try {
    dispatch(commonActionCreators.acSetLoadingIndicator(true));
    dispatch(actionCreators.acSetLogoutError(ErrorCodes.none));
    await ensureMsSsoToken();
    try {
      await api.logout();
    } catch (error) {
      // The backend session still exists, so clearing the cookies would only be undone by the session restore.
      // The user stays logged in and is told why (TEAM-1883).
      console.error('removeAuthentication: backend logout failed', error);
      dispatch(actionCreators.acSetLogoutError(toHttpErrorCode(error)));
      return;
    }

    resetSession();
    dispatch(actionCreators.acSetIsAuthenticated(false));

    const loginUrl = (await api.getLoginUrl()).data.loginUrl;
    dispatch(actionCreators.acSetAuthUrl(loginUrl));
  } catch (error) {
    console.error('removeAuthentication', error);
  } finally {
    dispatch(commonActionCreators.acSetLoadingIndicator(false));
  }
};

const removeSmgAuthentication = () => async (dispatch: (action: BaseAction) => void) => {
  try {
    dispatch(commonActionCreators.acSetLoadingIndicator(true));
    resetSession();
  } catch (error) {
    console.error('removeSmgAuthentication', error);
  } finally {
    dispatch(commonActionCreators.acSetLoadingIndicator(false));
  }
};

// ssoProvider is only passed by tests; the app always talks to the real Teams client.
const checkAuthentication = (ssoProvider?: MsSsoTokenProvider) => async (dispatch: (action: BaseAction) => void) => {
  try {
    dispatch(commonActionCreators.acSetLoadingIndicator(true));

    const msTeamsToken = await ensureMsSsoToken(ssoProvider);
    let smgToken: SmgToken | null = storageHelper.getSmgToken();
    let restoreFailedTransiently = false;
    if (!isSmgSessionUsable(smgToken)) {
      // The Teams desktop client drops cookies on restart while the backend still holds the token pair for this
      // Teams user, so restore it before giving up and showing the login page (TEAM-1883). Stale cookies are
      // removed first, otherwise the interceptor would attempt a doomed refresh on the restore call itself.
      storageHelper.removeSmgToken();
      smgToken = null;
      if (msTeamsToken) {
        const restored = await restoreSmgSession();
        smgToken = restored.smgToken;
        restoreFailedTransiently = restored.transientFailure;
      }
    }

    if (!msTeamsToken || !smgToken) {
      if (restoreFailedTransiently) {
        // The backend may still hold the session; tell the user why the login page appears.
        dispatch(actionCreators.setAuthenticationError(ErrorCodes.errNetwork));
      }

      dispatch(actionCreators.acSetIsAuthenticated(false));
      const loginUrl = (await api.getLoginUrl()).data.loginUrl;
      dispatch(actionCreators.acSetAuthUrl(loginUrl));
      return;
    }

    const response = await api.isAuthenticated();
    const isAuthenticated = response.data;
    dispatch(actionCreators.acSetIsAuthenticated(isAuthenticated));

    if (!isAuthenticated) {
      resetSession();
      const loginUrl = (await api.getLoginUrl()).data.loginUrl;
      dispatch(actionCreators.acSetAuthUrl(loginUrl));
      dispatch(actionCreators.acSetIsAuthenticated(false));
    }
  } catch (error) {
    if (api.isSmgAuthBaseNotConfiguredError(error)) {
      dispatch(actionCreators.setAuthenticationError(ErrorCodes.errSmgAuthNotConfigured));
    } else {
      dispatch(actionCreators.setAuthenticationError(toHttpErrorCode(error)));
    }
  } finally {
    dispatch(commonActionCreators.acSetLoadingIndicator(false));
  }
};

const resetSession = async () => {
  const refreshToken = storageHelper.getSmgRefreshToken();
  await api.logoutFromSmg(refreshToken);
  storageHelper.removeSmgToken();
  storageHelper.removeMsSsoToken();
};

// Maps a failed request to the error code shown to the user. No response (offline, timeout, backend down) and
// unexpected statuses both read as a connection problem.
const toHttpErrorCode = (error: unknown): ErrorCode => {
  switch ((error as AxiosError).response?.status) {
    case httpStatusCodes.BAD_REQUEST:
      return ErrorCodes.errBr400;
    case httpStatusCodes.UNAUTHORIZED:
      return ErrorCodes.errBr401;
    case httpStatusCodes.NOT_FOUND:
      return ErrorCodes.errBr404;
    case 500:
      return ErrorCodes.errBr500;
    default:
      return ErrorCodes.errNetwork;
  }
};

// The access token may be expired (the interceptor refreshes it) but must decode; the refresh token must still be valid.
const isSmgSessionUsable = (smgToken: SmgToken | null): boolean =>
  !!smgToken && isTokenDecodable(smgToken.accessToken) && isTokenUsable(smgToken.refreshToken);

interface RestoreResult {
  smgToken: SmgToken | null;
  // True when the backend could not be asked (network error, 5xx), as opposed to "no session stored".
  transientFailure: boolean;
}

const restoreSmgSession = async (): Promise<RestoreResult> => {
  try {
    const response = await api.getSmgToken();
    const smgToken = response.data;
    if (response.status === httpStatusCodes.OK && smgToken?.accessToken && smgToken?.refreshToken) {
      storageHelper.setSmgToken(smgToken);

      return { smgToken, transientFailure: false };
    }

    return { smgToken: null, transientFailure: false };
  } catch (error) {
    // 404: nothing stored for this Teams user. 401: the SSO token was rejected. Both simply end on the login page.
    const status = (error as AxiosError).response?.status;
    const transientFailure = status !== httpStatusCodes.NOT_FOUND && status !== httpStatusCodes.UNAUTHORIZED;
    if (transientFailure) {
      console.error('restoreSmgSession', error);
    }

    return { smgToken: null, transientFailure };
  }
};

export { doAuthentication, checkAuthentication, removeAuthentication, removeSmgAuthentication };
